import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { CYCLE_YEAR } from '../../config/recruitingCycle';

const MultiSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const removeValue = (optionValue, e) => {
    e.stopPropagation();
    const newValue = value.filter((v) => v !== optionValue);
    onChange(newValue);
  };

  const handleClickOutside = (e) => {
    if (isOpen && !e.target.closest('.multi-select-container')) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative multi-select-container">
      <div
        className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus-within:border-blue-500 focus-within:ring-blue-500 cursor-pointer min-h-[42px] flex items-center hover:border-gray-300 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 w-full">
          {value.length > 0 ? (
            value.map((v) => {
              const option = options.find((opt) => opt.value === v);
              return option ? (
                <span
                  key={v}
                  className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={(e) => removeValue(v, e)}
                    className="inline-flex items-center justify-center size-4 rounded-full text-blue-800 hover:bg-blue-200 focus:outline-hidden focus:bg-blue-200"
                  >
                    <svg
                      className="shrink-0 size-3"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m18 6-12 12" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </span>
              ) : null;
            })
          ) : (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          )}
        </div>
        <div className="ml-auto flex items-center">
          <svg
            className={`shrink-0 size-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <input
            type="text"
            className="py-2.5 px-4 block w-full border-b border-gray-200 rounded-t-lg text-sm focus:outline-hidden focus:border-blue-500 focus:ring-blue-500"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="max-h-60 overflow-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`flex items-center py-2.5 px-4 text-sm hover:bg-gray-50 cursor-pointer transition-colors ${
                  value.includes(option.value) ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleSelect(option.value)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => {}}
                  className="shrink-0 mt-0.5 border-gray-200 rounded-sm text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                />
                <span className="ms-3 text-sm text-gray-800">{option.label}</span>
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="py-2.5 px-4 text-sm text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const JobFilterModal = ({ isOpen, onClose, filters, onApplyFilters }) => {
  const [localFilters, setLocalFilters] = useState({
    ...filters,
    jobFocus: Array.isArray(filters.jobFocus) ? filters.jobFocus.filter(Boolean) : [],
    visaStatus: Array.isArray(filters.visaStatus) ? filters.visaStatus.filter(Boolean) : [],
    workLocation: Array.isArray(filters.workLocation) ? filters.workLocation.filter(Boolean) : [],
    internshipSeason: Array.isArray(filters.internshipSeason)
      ? filters.internshipSeason.filter(Boolean)
      : [],
    housingStatus: Array.isArray(filters.housingStatus)
      ? filters.housingStatus.filter(Boolean)
      : [],
    minEducationLevel: filters.minEducationLevel || '',
    minGpa: filters.minGpa || '',
    gradYear: filters.minGradYear || '',
    requiresSecurityClearance: filters.requiresSecurityClearance || false,
    companySize: Array.isArray(filters.companySize) ? filters.companySize.filter(Boolean) : [],
  });

  const handleInputChange = (field, value) => {
    const processedValue = Array.isArray(value) ? value.filter(Boolean) : value;
    setLocalFilters((prev) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleApply = () => {
    // Create a new object with only non-empty values
    const processedFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length > 0) {
          acc[key] = value;
        }
      }
      // Handle numbers
      else if (typeof value === 'number') {
        if (value > 0) {
          acc[key] = value;
        }
      }
      // Handle strings
      else if (typeof value === 'string') {
        if (value.trim()) {
          if (key === 'gradYear') {
            // Convert gradYear to both minGradYear and maxGradYear for backend compatibility
            // This ensures we only get roles where the student's graduation year falls within the range
            const yearValue = parseInt(value);
            acc['minGradYear'] = yearValue;
            acc['maxGradYear'] = yearValue;
          } else {
            acc[key] = value;
          }
        }
      }
      // Handle booleans
      else if (typeof value === 'boolean') {
        if (value === true) {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    // Convert numeric strings to numbers
    if (processedFilters.minGpa) {
      processedFilters.minGpa = parseFloat(processedFilters.minGpa);
    }

    console.log('Sending filters:', processedFilters);
    onApplyFilters(processedFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters({
      jobFocus: [],
      visaStatus: [],
      minEducationLevel: '',
      workLocation: [],
      internshipSeason: [],
      housingStatus: [],
      minGpa: '',
      gradYear: '',
      requiresSecurityClearance: false,
      companySize: [],
    });
  };

  const jobFocusOptions = [
    { value: 'Frontend', label: 'Frontend' },
    { value: 'Backend', label: 'Backend' },
    { value: 'Full Stack', label: 'Full Stack' },
    { value: 'Mobile', label: 'Mobile' },
    { value: 'DevOps', label: 'DevOps' },
    { value: 'ML/AI', label: 'ML/AI' },
    { value: 'Data Engineering', label: 'Data Engineering' },
    { value: 'Security', label: 'Security' },
    { value: 'Embedded Systems', label: 'Embedded Systems' },
    { value: 'Cloud', label: 'Cloud' },
  ];

  const visaStatusOptions = [
    { value: 'US Citizens Only', label: 'US Citizens Only' },
    { value: 'Accepts OPT/CPT', label: 'Accepts OPT/CPT' },
    { value: 'Sponsors H1B', label: 'Sponsors H1B' },
  ];

  const workLocationOptions = [
    { value: 'On-site', label: 'On-site' },
    { value: 'Hybrid', label: 'Hybrid' },
    { value: 'Remote', label: 'Remote' },
  ];

  const internshipSeasonOptions = [
    { value: 'Summer', label: 'Summer' },
    { value: 'Fall', label: 'Fall' },
    { value: 'Winter', label: 'Winter' },
    { value: 'Spring', label: 'Spring' },
    { value: 'Year-round', label: 'Year-round' },
  ];

  const housingStatusOptions = [
    { value: 'Housing Provided', label: 'Housing Provided' },
    { value: 'Housing Stipend', label: 'Housing Stipend' },
    { value: 'No Housing Support', label: 'No Housing Support' },
  ];

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-250', label: '101-250 employees' },
    { value: '251-500', label: '251-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1001-5000', label: '1001-5000 employees' },
    { value: '5001-10000', label: '5001-10000 employees' },
    { value: '10000+', label: '10000+ employees' },
  ];

  // Add useEffect to sync filters with parent component
  useEffect(() => {
    setLocalFilters((prev) => ({
      ...prev,
      ...filters,
      // Ensure arrays are properly initialized
      jobFocus: Array.isArray(filters.jobFocus) ? filters.jobFocus.filter(Boolean) : [],
      visaStatus: Array.isArray(filters.visaStatus) ? filters.visaStatus.filter(Boolean) : [],
      workLocation: Array.isArray(filters.workLocation) ? filters.workLocation.filter(Boolean) : [],
      internshipSeason: Array.isArray(filters.internshipSeason)
        ? filters.internshipSeason.filter(Boolean)
        : [],
      housingStatus: Array.isArray(filters.housingStatus)
        ? filters.housingStatus.filter(Boolean)
        : [],
      companySize: Array.isArray(filters.companySize) ? filters.companySize.filter(Boolean) : [],
    }));
  }, [filters]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-60 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="px-4 py-6 sm:px-6 lg:px-8 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Filter Jobs</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Apply filters to find internships that match your preferences.
                </p>
              </div>
              <button
                onClick={onClose}
                type="button"
                className="inline-flex justify-center items-center size-8 text-sm font-semibold rounded-lg border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="shrink-0 size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-6 sm:px-6 lg:px-8 max-h-[70vh] overflow-y-auto">
            <div className="space-y-8">
              {/* Job Preferences Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Job Preferences</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* Job Focus */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Job Focus
                    </label>
                    <MultiSelect
                      options={jobFocusOptions}
                      value={localFilters.jobFocus}
                      onChange={(value) => handleInputChange('jobFocus', value)}
                      placeholder="Select job focus..."
                    />
                  </div>

                  {/* Work Location */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Work Location
                    </label>
                    <MultiSelect
                      options={workLocationOptions}
                      value={localFilters.workLocation}
                      onChange={(value) => handleInputChange('workLocation', value)}
                      placeholder="Select work location..."
                    />
                  </div>

                  {/* Internship Season */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Internship Season
                    </label>
                    <MultiSelect
                      options={internshipSeasonOptions}
                      value={localFilters.internshipSeason}
                      onChange={(value) => handleInputChange('internshipSeason', value)}
                      placeholder="Select internship season..."
                    />
                  </div>

                  {/* Company Size */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Company Size
                    </label>
                    <MultiSelect
                      options={companySizeOptions}
                      value={localFilters.companySize}
                      onChange={(value) => handleInputChange('companySize', value)}
                      placeholder="Select company size..."
                    />
                  </div>
                </div>
              </div>

              {/* Requirements Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Requirements & Eligibility
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* Visa Status */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Visa Status
                    </label>
                    <MultiSelect
                      options={visaStatusOptions}
                      value={localFilters.visaStatus}
                      onChange={(value) => handleInputChange('visaStatus', value)}
                      placeholder="Select visa status..."
                    />
                  </div>

                  {/* Education Level */}
                  <div>
                    <label
                      htmlFor="education-level"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Minimum Education Level
                    </label>
                    <select
                      id="education-level"
                      value={localFilters.minEducationLevel}
                      onChange={(e) => handleInputChange('minEducationLevel', e.target.value)}
                      className="py-2.5 sm:py-3 px-4 pe-9 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <option value="">All</option>
                      <option value="High School">High School</option>
                      <option value="Bachelor">Bachelor's</option>
                      <option value="Master">Master's</option>
                      <option value="PhD">PhD</option>
                    </select>
                  </div>

                  {/* Minimum GPA */}
                  <div>
                    <label
                      htmlFor="min-gpa"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Minimum GPA
                    </label>
                    <input
                      type="number"
                      id="min-gpa"
                      min="0"
                      max="4"
                      step="0.1"
                      value={localFilters.minGpa}
                      onChange={(e) => handleInputChange('minGpa', e.target.value)}
                      className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      placeholder="e.g., 3.0"
                    />
                  </div>

                  {/* Graduation Year */}
                  <div>
                    <label
                      htmlFor="grad-year"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Graduation Year
                    </label>
                    <input
                      type="number"
                      id="grad-year"
                      min={CYCLE_YEAR - 3}
                      max={CYCLE_YEAR + 6}
                      value={localFilters.gradYear}
                      onChange={(e) => handleInputChange('gradYear', e.target.value)}
                      className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      placeholder={`e.g., ${CYCLE_YEAR + 1}`}
                    />
                  </div>
                </div>

                {/* Security Clearance Checkbox */}
                <div className="mt-6">
                  <div className="flex">
                    <div className="flex">
                      <input
                        id="security-clearance"
                        name="security-clearance"
                        type="checkbox"
                        checked={localFilters.requiresSecurityClearance}
                        onChange={(e) =>
                          handleInputChange('requiresSecurityClearance', e.target.checked)
                        }
                        className="shrink-0 mt-0.5 border-gray-200 rounded-sm text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      />
                    </div>
                    <div className="ms-3">
                      <label
                        htmlFor="security-clearance"
                        className="text-sm font-medium text-gray-700"
                      >
                        Requires Security Clearance
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits & Compensation Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Benefits & Compensation
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  {/* Housing Status */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Housing Status
                    </label>
                    <MultiSelect
                      options={housingStatusOptions}
                      value={localFilters.housingStatus}
                      onChange={(value) => handleInputChange('housingStatus', value)}
                      placeholder="Select housing status..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-4 sm:px-6 lg:px-8 border-t border-gray-200">
            <div className="flex justify-end gap-x-2">
              <button
                type="button"
                onClick={handleReset}
                className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('modal-root')
  );
};

export default JobFilterModal;
